/**
 * @desc
 * - Service for use with the vulnerability trend chart code
 * - Builds view data
 */
;(function () {
  'use strict'

  angular
    .module('contrast.vulnerabilities.trend')
    .factory('csVulnTrendChartService', csVulnTrendChartService)

  function csVulnTrendChartService(
    d3,
    VULN_TREND_CHART_CONSTANTS,
    VULNERABILITY_REPORT_RANGES
  ) {
    'ngInject'

    var totalYAxisTicks = 20

    var service = {
      buildViewData: buildViewData,
      calculateYRange: calculateYRange
    }

    return service

    function buildViewData(data, rangeStr, isMini) {
      var viewData = angular.copy(data)
      viewData.isMini = isMini

      // The order of this array dictates the vertical orientation of the chart.
      // Index 0 === top
      // Index 1 === bottom
      viewData.order = [
        VULN_TREND_CHART_CONSTANTS.CLOSED,
        VULN_TREND_CHART_CONSTANTS.OPEN
      ]

      var byTimestamp = {}
      byTimestamp[VULN_TREND_CHART_CONSTANTS.OPEN] = {}
      byTimestamp[VULN_TREND_CHART_CONSTANTS.CLOSED] = {}

      viewData.totalDiscovered = calculateTotalBreakdownSum(data.open)
      viewData.totalRemediated = calculateTotalBreakdownSum(data.closed)

      viewData.rangeLabel = VULNERABILITY_REPORT_RANGES.find(function (obj) {
        return obj.keycode === rangeStr
      }).translationId

      process(VULN_TREND_CHART_CONSTANTS.OPEN)
      process(VULN_TREND_CHART_CONSTANTS.CLOSED)

      function process(key) {
        viewData[key] = []
        var obj
        for (var i = 0; i < data[key].length; i++) {
          obj = angular.copy(data[key][i])
          obj.timeObj = moment(obj.timestamp / 1000)
          obj.which = key
          if (key === viewData.order[0] && obj.count !== 0) {
            obj.count = -Math.abs(obj.count)
          }
          viewData[key].push(obj)

          obj = _parseStatusNames(obj)

          byTimestamp[key][obj.timestamp] = obj
        }
      }

      calculateYRange(viewData)
      viewData.byTimestamp = byTimestamp
      viewData.timeRange = _determineTimeRange(rangeStr)
      viewData.xAxisData = _determineXAxisData(viewData, rangeStr, isMini)
      viewData.yAxisData = _determineYAxisData(viewData)
      viewData.lineChartConfigs = _buildLineChartConfigs(viewData)

      return viewData
    }

    function calculateTotalBreakdownSum(objects) {
      return objects.reduce(function (total, obj) {
        return (
          total +
          obj.statusBreakdown.reduce(function (breakdownSum, breakdownObj) {
            return breakdownSum + breakdownObj.value
          }, 0)
        )
      }, 0)
    }

    function calculateYRange(viewData) {
      var concat = viewData[VULN_TREND_CHART_CONSTANTS.OPEN].concat(
        viewData[VULN_TREND_CHART_CONSTANTS.CLOSED]
      )
      var max = d3.max(concat, function (d) {
        return d.count
      })
      var min = d3.min(concat, function (d) {
        return d.count
      })
      viewData.yRange = [min, max]
    }

    function _buildLineChartConfigs(viewData) {
      return [
        {
          which: VULN_TREND_CHART_CONSTANTS.CUSTOM_X_AXIS,
          data: _buildCustomXAxisDataFromTimeRange(viewData)
        },
        {
          which: viewData.order[0]
        },
        {
          which: viewData.order[1]
        }
      ]
    }

    function _parseStatusNames(obj) {
      obj.statusBreakdown = obj.statusBreakdown.map(function (sb) {
        sb.viewName = sb.name.split(/(?=[A-Z])/).join(' ')
        return sb
      })
      return obj
    }

    function _determineTimeRange(rangeStr) {
      var result = VULNERABILITY_REPORT_RANGES.find(function (o) {
        return o.keycode === rangeStr
      })
      if (!result) {
        var validKeys = VULNERABILITY_REPORT_RANGES.map(function (o) {
          return o.keycode
        }).join(',')
        throw new Error(
          'Invalid key used for determineTimeRange. Valid keys: [' +
            validKeys +
            '].'
        )
      }
      return {
        start: moment().subtract(
          result.momentConfig.val,
          result.momentConfig.period
        ),
        end: moment()
      }
    }

    function _buildCustomXAxisDataFromTimeRange(viewData) {
      var domain = viewData.xAxisData.domain,
        start = domain[0],
        end = domain[1]
      return [
        {
          timeStr: moment(start).toDate(),
          timestamp: moment(start).valueOf(),
          count: 0
        },
        {
          timeStr: moment(end).toDate(),
          timestamp: moment(end).valueOf(),
          count: 0
        }
      ]
    }

    function _determineXAxisData(viewData, rangeStr, isMini) {
      if (isMini) {
        return _determineXAxisDataMini(viewData, rangeStr)
      }
      return _determineXAxisDataNormal(viewData, rangeStr)
    }

    function _determineYAxisData(viewData) {
      var yRange = viewData.yRange
      var min = yRange[0]
      var max = yRange[1]
      var values = [min, max]
      var range = d3.scale
        .linear()
        .domain(values)
        .range(values)
        .ticks(totalYAxisTicks)
      return {
        tickValues: range,
        formatter: function (v) {
          if (v % 1) {
            return ''
          }
          return Math.abs(v)
        }
      }
    }

    function _determineXAxisDataNormal(viewData, rangeStr) {
      var range = viewData.timeRange
      var startDate = range.start.toDate(),
        endDate = range.end.toDate(),
        values = [],
        domain = [startDate, endDate],
        formatter
      switch (rangeStr) {
        case VULNERABILITY_REPORT_RANGES[3].keycode: // year
          values = d3.time.months(startDate, endDate)
          formatter = function (val) {
            return d3.time.format('%b')(val).toUpperCase()
          }
          break
        case VULNERABILITY_REPORT_RANGES[2].keycode: // quarter
          values = d3.time.weeks(startDate, endDate)
          formatter = function (val) {
            return d3.time.format('%b %e')(val).toUpperCase()
          }
          break
        case VULNERABILITY_REPORT_RANGES[1].keycode: // month
          values = d3.time.days(startDate, endDate).filter(function (v, i) {
            return i % 3 === 0
          })
          formatter = function (val) {
            return d3.time.format('%b %e')(val).toUpperCase()
          }
          break
        case VULNERABILITY_REPORT_RANGES[0].keycode: // week
        default:
          formatter = function (val) {
            return d3.time.format('%b %e')(val).toUpperCase()
          }
          values = d3.time.days(startDate, endDate)
          break
      }
      return {
        values: values,
        formatter: formatter,
        domain: domain
      }
    }

    function _determineXAxisDataMini(viewData, rangeStr) {
      var range = viewData.timeRange
      var startDate = range.start.toDate(),
        endDate = range.end.toDate(),
        values = [],
        tickValues,
        domain = [startDate, endDate],
        formatter
      switch (rangeStr) {
        case VULNERABILITY_REPORT_RANGES[3].keycode: // year
          var allMonths = d3.time.months(startDate, endDate)
          values = allMonths.filter(function (a, i) {
            return i % 2 == 0
          })
          formatter = function (val) {
            return d3.time.format('%b')(val).toUpperCase()
          }
          break
        case VULNERABILITY_REPORT_RANGES[1].keycode: // month
          var weekday = d3.time.format('%A')(new Date()).toLowerCase()
          var valueStart = moment(startDate).subtract(2, 'd').toDate()
          var valueEnd = moment(endDate).add(2, 'd').toDate()
          values = d3.time.days(valueStart, valueEnd)
          tickValues = d3.time[weekday + 's'](startDate, endDate)
          domain = [values[0], values[values.length - 1]]
          formatter = function (val) {
            return d3.time.format('%b %e')(val).toUpperCase()
          }
          break
        case VULNERABILITY_REPORT_RANGES[0].keycode: // week
        default:
          values = d3.time.days(startDate, endDate)
          formatter = function (val) {
            return d3.time.format('%a')(val).toUpperCase()
          }
          break
      }
      return {
        values: values,
        tickValues: tickValues,
        formatter: formatter,
        domain: domain
      }
    }
  }
})()
